import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { OrgUserRole } from '~/app/landing/people/utils';
import { SiteService } from '~/app/services/site/site.service';
import { UserService } from '~/app/services/user/user.service';

export function useWorkOrderKanbanLookups(organizationId: string) {
  const sitesQuery = useQuery({
    queryKey: ['sites', 'lookup', organizationId],
    queryFn: () => SiteService.findAllByOrganizationId(organizationId),
    enabled: Boolean(organizationId),
  });

  const techniciansQuery = useQuery({
    queryKey: ['users', 'technicians', organizationId],
    queryFn: () =>
      UserService.listOrganizationUsers(
        organizationId,
        OrgUserRole.FIELD_TECHNICIAN,
      ),
    enabled: Boolean(organizationId),
  });

  const siteNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const site of sitesQuery.data ?? []) {
      map.set(site.id, site.name);
    }
    return map;
  }, [sitesQuery.data]);

  const technicianNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of techniciansQuery.data ?? []) {
      map.set(user.id, user.name);
    }
    return map;
  }, [techniciansQuery.data]);

  const siteFilterOptions = useMemo(
    () =>
      (sitesQuery.data ?? []).map((site) => ({
        value: site.id,
        label: site.name,
      })),
    [sitesQuery.data],
  );

  const technicianOptions = useMemo(
    () =>
      (techniciansQuery.data ?? [])
        .filter((user) => user.active)
        .map((user) => ({ value: user.id, label: user.name })),
    [techniciansQuery.data],
  );

  return {
    siteNameById,
    technicianNameById,
    siteFilterOptions,
    technicianOptions,
    isLoadingLookups: sitesQuery.isLoading || techniciansQuery.isLoading,
  };
}
